import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Rocket, Search as SearchIcon, Loader2, Plus, TrendingUp, MapPin, Github, Globe, Play,
  ExternalLink, Sparkles, Heart, HandCoins, Users, X, Pencil, Trash2, Store, ShoppingBag,
  Target, Layers, ImagePlus, FileText, Building2, ThumbsUp, Share2, Coins, Download,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STAGES = [
  { key: "idea", label: "Idea" },
  { key: "prototype", label: "Prototype" },
  { key: "pre_revenue", label: "Pre-revenue" },
  { key: "revenue", label: "Revenue" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));
const CUR_SYM = { USD: "$", EUR: "€", INR: "₹", GBP: "£", BTC: "₿", AED: "AED ", SGD: "S$" };
const money = (n, c = "USD") => `${CUR_SYM[c] || c + " "}${Number(n || 0).toLocaleString()}`;
const imgUrl = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const EMPTY = {
  listing_type: "venture", name: "", one_liner: "", description: "", logo: "", cover: "", screenshots: [],
  stage: "idea", year_started: "", growth: "", potential: "", sector: "", country: "", website: "",
  github: "", repository: "", github_profile: "", demo_url: "", video_url: "", pitch_deck_url: "", files: [],
  team_size: "", traction: "", contact_email: "", contact_telegram: "", tags: [], funding_goal: "", funding_currency: "USD", equity_offered: "",
  sale_price: "", sale_currency: "USD", license_type: "", tech_stack: "", platform: "Mobile Web",
};

function StageBadge({ stage }) {
  return <span className="text-[10px] uppercase tracking-widest text-brand border border-brand/40 rounded-full px-2 py-0.5">{STAGE_LABEL[stage] || stage}</span>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-sec">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function VentureCard({ v, onOpen }) {
  const isApp = v.listing_type === "app";
  const pct = v.funding_goal > 0 ? Math.min(100, Math.round((v.raised / v.funding_goal) * 100)) : 0;
  return (
    <motion.button
      layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`venture-card-${v.id}`} onClick={() => onOpen(v)}
      className="text-left rud-card rounded-2xl overflow-hidden group flex flex-col">
      <div className="h-24 bg-elevated relative overflow-hidden">
        {v.cover ? <img src={imgUrl(v.cover)} alt="" className="w-full h-full object-cover" /> :
          <div className="w-full h-full bg-gradient-to-br from-[var(--rud-brand)]/20 via-elevated to-elevated" />}
        {typeof v.match_score === "number" && (
          <span data-testid={`match-score-${v.id}`} className="absolute top-2 right-2 text-[11px] font-semibold bg-brand text-black rounded-full px-2 py-0.5">{v.match_score}% match</span>
        )}
        <span className="absolute bottom-2 left-2"><StageBadge stage={v.stage} /></span>
      </div>
      <div className="p-5 -mt-8 flex-1 flex flex-col">
        <div className="w-12 h-12 rounded-xl bg-elevated border border-rud grid place-items-center overflow-hidden">
          {v.logo ? <img src={imgUrl(v.logo)} alt="" className="w-full h-full object-cover" /> :
            (isApp ? <ShoppingBag size={18} className="text-sec" /> : <Rocket size={18} className="text-sec" />)}
        </div>
        <div className="font-medium mt-3 group-hover:text-brand transition-colors line-clamp-1">{v.name}</div>
        {v.one_liner && <p className="text-xs text-sec mt-1 line-clamp-2">{v.one_liner}</p>}
        {v.match_why && <p className="text-[11px] text-brand mt-1.5 line-clamp-2 flex gap-1"><Sparkles size={12} className="shrink-0 mt-0.5" />{v.match_why}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-sec">
          {v.sector && <span className="text-brand">{v.sector}</span>}
          {v.country && <span className="flex items-center gap-1"><MapPin size={11} /> {v.country}</span>}
          {v.growth && <span className="flex items-center gap-1"><TrendingUp size={11} /> {v.growth}</span>}
        </div>
        <div className="mt-auto pt-3">
          {isApp ? (
            <div className="flex items-center justify-between">
              <span className="text-brand font-semibold">{v.sale_price > 0 ? money(v.sale_price, v.sale_currency) : "Make an offer"}</span>
              <span className="text-[11px] text-sec">{v.license_type || v.platform}</span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-sec">{money(v.raised, v.funding_currency)} raised</span>
                <span className="text-sec">Goal {money(v.funding_goal, v.funding_currency)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-elevated overflow-hidden"><div className="h-full bg-brand" style={{ width: `${pct}%` }} /></div>
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-sec">
                <span className="flex items-center gap-1"><Users size={11} /> {v.backers || 0} backers</span>
                <span className="flex items-center gap-1"><Heart size={11} /> {v.interest_count || 0} interested</span>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.button>
  );
}

export default function Ventures() {
  const { user, refreshUser } = useApp();
  const [mode, setMode] = useState("venture"); // venture | app | mine
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const [stage, setStage] = useState("");
  const [country, setCountry] = useState("");
  const [sort, setSort] = useState("recent");
  const [sectors, setSectors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchMode, setMatchMode] = useState(false);

  // investor focus
  const [showFocus, setShowFocus] = useState(false);
  const [focusSectors, setFocusSectors] = useState("");
  const [keywords, setKeywords] = useState("");
  const [prefStages, setPrefStages] = useState([]);
  const [thesis, setThesis] = useState("");
  const [matching, setMatching] = useState(false);

  // dialogs
  const [postOpen, setPostOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [backers, setBackers] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setMatchMode(false);
    try {
      if (mode === "mine") {
        const { data } = await api.get("/ventures/mine");
        setItems(data.ventures || []);
      } else {
        const { data } = await api.get("/ventures", { params: { listing_type: mode, q, sector, stage, country, sort } });
        setItems(data.ventures || []); setSectors(data.sectors || []);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sector, stage, country, sort]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) return;
    api.get("/investor/profile").then(({ data }) => {
      const p = data.investor_profile || {};
      if (p.focus_sectors?.length) setFocusSectors(p.focus_sectors.join(", "));
      if (p.problem_keywords) setKeywords(p.problem_keywords);
      if (p.preferred_stages?.length) setPrefStages(p.preferred_stages);
      if (p.thesis) setThesis(p.thesis);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("id");
    if (id) setDetail({ id, is_owner: false });
  }, []);

  const runMatch = async () => {
    if (!user) { toast.error("Sign in to run AI match."); return; }
    setMatching(true);
    try {
      const payload = {
        focus_sectors: focusSectors.split(",").map((s) => s.trim()).filter(Boolean),
        problem_keywords: keywords, preferred_stages: prefStages, thesis,
        listing_type: mode === "app" ? "app" : "venture",
      };
      const { data } = await api.post("/ventures/ai-match", payload);
      setItems(data.matches || []); setMatchMode(true);
      api.post("/investor/profile", payload).catch(() => {});
      if (data.charged) { refreshUser?.(); }
      if (!(data.matches || []).length) toast("No listings to match yet — be the first to post one!");
    } catch (e) {
      if (e.response?.status === 402) toast.error(apiError(e.response?.data?.detail));
      else toast.error(apiError(e.response?.data?.detail));
    }
    finally { setMatching(false); }
  };

  const openDetail = (v) => {
    if (!user && !v.is_owner) { toast.error("Sign in to open the full idea (viewing costs a few credits)."); return; }
    setDetail(v);
  };

  const togglePref = (k) => setPrefStages((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);

  const openPost = (v) => {
    if (!user) { toast.error("Sign in to post a listing."); return; }
    setEditing(v || { ...EMPTY, listing_type: mode === "app" ? "app" : "venture" });
    setPostOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Rocket size={14} /> Crowdfund · Ventures & App Market</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Raise funds. Sell your app. Back the next big thing.</h1>
        <p className="text-sec mt-2 text-sm">Founders host their project on My Kind and seek funding. Builders list mobile web apps for sale. Investors discover ideas that match their thesis and challenges — with AI-powered matching.</p>
      </motion.div>

      {/* Mode tabs */}
      <div className="flex flex-wrap items-center gap-2 mt-6">
        <div className="flex items-center gap-1 rounded-full border border-rud p-0.5 text-sm">
          {[{ id: "venture", label: "Raise & Fund", icon: HandCoins }, { id: "app", label: "App Market", icon: Store }, { id: "mine", label: "My Listings", icon: Layers }].map((tb) => (
            <button key={tb.id} data-testid={`ventures-tab-${tb.id}`} onClick={() => setMode(tb.id)}
              className={`px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${mode === tb.id ? "bg-brand text-black font-semibold" : "text-sec"}`}>
              <tb.icon size={14} /> {tb.label}
            </button>
          ))}
        </div>
        <button data-testid="ventures-post-btn" onClick={() => openPost(null)} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5 ml-auto">
          <Plus size={15} /> {mode === "app" ? "List an app" : "Post a project"}
        </button>
      </div>

      {mode !== "mine" && (
        <>
          {/* Search + filters */}
          <div className="flex flex-wrap items-center gap-2 mt-5">
            <div className="relative flex-1 min-w-[220px]">
              <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec" />
              <input data-testid="ventures-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search projects, apps, sectors…" className="rud-input w-full rounded-full pl-10 pr-4 py-2.5 text-sm" />
            </div>
            <select data-testid="ventures-sector" value={sector} onChange={(e) => setSector(e.target.value)} className="rud-input rounded-lg px-3 py-2 text-sm">
              <option value="">All sectors</option>
              {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="ventures-stage" value={stage} onChange={(e) => setStage(e.target.value)} className="rud-input rounded-lg px-3 py-2 text-sm">
              <option value="">All stages</option>
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select data-testid="ventures-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rud-input rounded-lg px-3 py-2 text-sm">
              <option value="recent">Newest</option>
              <option value="funded">Most funded</option>
              <option value="interest">Most interest</option>
            </select>
          </div>

          {/* Investor focus / AI match */}
          <div className="rud-card rounded-2xl p-4 mt-4" data-testid="investor-focus">
            <button onClick={() => setShowFocus((v) => !v)} className="w-full flex items-center gap-2 text-sm">
              <Target size={16} className="text-brand" />
              <span className="font-medium">Investor focus — find ideas that match your thesis</span>
              <span className="ml-auto text-xs text-sec">{showFocus ? "Hide" : "Set up AI match"}</span>
            </button>
            {showFocus && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <Field label="Focus sectors (comma separated)">
                  <input data-testid="focus-sectors" value={focusSectors} onChange={(e) => setFocusSectors(e.target.value)} placeholder="Fintech, Health, AI" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                </Field>
                <Field label="Problems / challenges you care about">
                  <input data-testid="focus-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="fraud detection, rural logistics…" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                </Field>
                <Field label="Preferred stages">
                  <div className="flex flex-wrap gap-1.5">
                    {STAGES.map((s) => (
                      <button key={s.key} data-testid={`pref-stage-${s.key}`} onClick={() => togglePref(s.key)}
                        className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${prefStages.includes(s.key) ? "border-brand bg-brand/10 text-brand" : "border-rud text-sec"}`}>{s.label}</button>
                    ))}
                  </div>
                </Field>
                <Field label="Your investment thesis (optional)">
                  <input data-testid="focus-thesis" value={thesis} onChange={(e) => setThesis(e.target.value)} placeholder="I back early climate & AI infra startups in emerging markets" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                </Field>
                <div className="sm:col-span-2 flex items-center gap-2">
                  <button data-testid="run-ai-match" onClick={runMatch} disabled={matching} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2 disabled:opacity-60">
                    {matching ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Run AI Match
                  </button>
                  <span className="text-[11px] text-sec flex items-center gap-1"><Coins size={12} /> 4 credits per scan</span>
                  {matchMode && <button onClick={load} className="text-xs text-sec hover:text-brand">Clear match · show all</button>}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center gap-2 text-sec mt-10"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="ventures-empty">
          <Rocket size={30} className="mx-auto text-brand mb-3" />
          <p className="text-sec">{mode === "mine" ? "You haven't posted anything yet." : "Nothing here yet — be the first to post."}</p>
          <button onClick={() => openPost(null)} className="btn-primary rounded-full h-10 px-5 text-sm inline-flex items-center gap-2 mt-4"><Plus size={15} /> {mode === "app" ? "List your app" : "Post your project"}</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="ventures-grid">
          {items.map((v) => <VentureCard key={v.id} v={v} onOpen={openDetail} />)}
        </div>
      )}

      {postOpen && <PostDialog open={postOpen} onClose={() => setPostOpen(false)} initial={editing} onSaved={() => { setPostOpen(false); refreshUser?.(); load(); }} />}
      {detail && <DetailDialog v={detail} onClose={() => setDetail(null)} onEdit={(vv) => { setDetail(null); openPost(vv); }} onBackers={(vv) => { setDetail(null); setBackers(vv); }} onChanged={load} />}
      {backers && <BackersDialog v={backers} onClose={() => setBackers(null)} />}
    </div>
  );
}

/* ---------------- Post / edit dialog ---------------- */
function PostDialog({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isApp = f.listing_type === "app";

  const upload = async (file, key) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "general");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (key === "screenshots") set("screenshots", [...(f.screenshots || []), data.url]);
      else if (key === "files") set("files", [...(f.files || []), { name: file.name, url: data.url }]);
      else set(key, data.url);
    } catch (e) { toast.error("Upload failed."); }
    finally { setUploading(""); }
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Add a project name."); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        year_started: f.year_started ? parseInt(f.year_started, 10) : null,
        funding_goal: parseFloat(f.funding_goal || 0) || 0,
        sale_price: parseFloat(f.sale_price || 0) || 0,
        tags: Array.isArray(f.tags) ? f.tags : String(f.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.post("/ventures", payload);
      toast.success(f.id ? "Listing updated." : "Listing published — investors can now find it.");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-rud text-[var(--rud-text)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="venture-post-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight">{f.id ? "Edit listing" : "Post to the marketplace"}</DialogTitle></DialogHeader>

        <div className="flex items-center gap-1 rounded-full border border-rud p-0.5 text-sm w-fit">
          {[{ id: "venture", label: "Raise funds" }, { id: "app", label: "Sell an app" }].map((t) => (
            <button key={t.id} data-testid={`post-type-${t.id}`} onClick={() => set("listing_type", t.id)}
              className={`px-4 py-1.5 rounded-full ${f.listing_type === t.id ? "bg-brand text-black font-semibold" : "text-sec"}`}>{t.label}</button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div className="sm:col-span-2"><Field label="Project / app name"><input data-testid="post-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>
          <div className="sm:col-span-2"><Field label="One-liner (the idea in a sentence)"><input data-testid="post-oneliner" value={f.one_liner} onChange={(e) => set("one_liner", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>
          <div className="sm:col-span-2"><Field label="Full description / pitch"><textarea data-testid="post-description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={4} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>

          <Field label="Stage">
            <select data-testid="post-stage" value={f.stage} onChange={(e) => set("stage", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm">
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Year started"><input data-testid="post-year" type="number" value={f.year_started} onChange={(e) => set("year_started", e.target.value)} placeholder="2024" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Sector / industry"><input data-testid="post-sector" value={f.sector} onChange={(e) => set("sector", e.target.value)} placeholder="Fintech" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Country"><input data-testid="post-country" value={f.country} onChange={(e) => set("country", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Growth (e.g. 30% MoM)"><input data-testid="post-growth" value={f.growth} onChange={(e) => set("growth", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Team size"><input value={f.team_size} onChange={(e) => set("team_size", e.target.value)} placeholder="3 founders" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <div className="sm:col-span-2"><Field label="Market potential"><input data-testid="post-potential" value={f.potential} onChange={(e) => set("potential", e.target.value)} placeholder="$2B TAM, growing 20%/yr" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>
          <div className="sm:col-span-2"><Field label="Traction / metrics"><input value={f.traction} onChange={(e) => set("traction", e.target.value)} placeholder="1,200 users, $4k MRR" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>

          {isApp ? (
            <>
              <Field label="Sale price"><input data-testid="post-price" type="number" value={f.sale_price} onChange={(e) => set("sale_price", e.target.value)} placeholder="5000" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Currency"><input value={f.sale_currency} onChange={(e) => set("sale_currency", e.target.value.toUpperCase())} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="License type"><input value={f.license_type} onChange={(e) => set("license_type", e.target.value)} placeholder="Full ownership / source code" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Platform"><input value={f.platform} onChange={(e) => set("platform", e.target.value)} placeholder="Mobile Web / PWA" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
              <div className="sm:col-span-2"><Field label="Tech stack"><input value={f.tech_stack} onChange={(e) => set("tech_stack", e.target.value)} placeholder="React, FastAPI, MongoDB" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>
            </>
          ) : (
            <>
              <Field label="Funding goal"><input data-testid="post-goal" type="number" value={f.funding_goal} onChange={(e) => set("funding_goal", e.target.value)} placeholder="50000" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Currency"><input value={f.funding_currency} onChange={(e) => set("funding_currency", e.target.value.toUpperCase())} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
              <div className="sm:col-span-2"><Field label="Equity offered (optional)"><input value={f.equity_offered} onChange={(e) => set("equity_offered", e.target.value)} placeholder="8% for $50k" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field></div>
            </>
          )}

          <Field label="Company / project website"><input data-testid="post-website" value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://yourstartup.com" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Demo / live URL"><input data-testid="post-demo" value={f.demo_url} onChange={(e) => set("demo_url", e.target.value)} placeholder="https://" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="GitHub"><input value={f.github} onChange={(e) => set("github", e.target.value)} placeholder="https://github.com/…" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Repository"><input value={f.repository} onChange={(e) => set("repository", e.target.value)} placeholder="repo / GitLab / Bitbucket" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="GitHub profile link"><input data-testid="post-github-profile" value={f.github_profile} onChange={(e) => set("github_profile", e.target.value)} placeholder="https://github.com/yourhandle" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Pitch deck URL"><input value={f.pitch_deck_url} onChange={(e) => set("pitch_deck_url", e.target.value)} placeholder="link to deck" className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Contact email"><input value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Telegram / handle"><input value={f.contact_telegram} onChange={(e) => set("contact_telegram", e.target.value)} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></Field>

          {/* Media */}
          <Field label="Logo">
            <div className="flex items-center gap-2">
              {f.logo && <img src={imgUrl(f.logo)} alt="" className="w-10 h-10 rounded-lg object-cover border border-rud" />}
              <label className="btn-ghost rounded-lg h-9 px-3 text-xs flex items-center gap-1.5 cursor-pointer">
                {uploading === "logo" ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "logo")} />
              </label>
            </div>
          </Field>
          <Field label="Cover image">
            <div className="flex items-center gap-2">
              {f.cover && <img src={imgUrl(f.cover)} alt="" className="w-16 h-9 rounded-lg object-cover border border-rud" />}
              <label className="btn-ghost rounded-lg h-9 px-3 text-xs flex items-center gap-1.5 cursor-pointer">
                {uploading === "cover" ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "cover")} />
              </label>
            </div>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Screenshots">
              <div className="flex flex-wrap items-center gap-2">
                {(f.screenshots || []).map((s, i) => (
                  <div key={i} className="relative">
                    <img src={imgUrl(s)} alt="" className="w-16 h-16 rounded-lg object-cover border border-rud" />
                    <button onClick={() => set("screenshots", f.screenshots.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-black/70 rounded-full p-0.5"><X size={11} /></button>
                  </div>
                ))}
                <label className="btn-ghost rounded-lg w-16 h-16 grid place-items-center cursor-pointer">
                  {uploading === "screenshots" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "screenshots")} />
                </label>
              </div>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Documents & pitch deck files (PDF, PPT, docs)">
              <div className="flex flex-wrap items-center gap-2">
                {(f.files || []).map((doc, i) => (
                  <div key={i} className="flex items-center gap-1.5 rounded-lg border border-rud bg-elevated px-2.5 py-1.5 text-xs">
                    <FileText size={13} className="text-brand" /> <span className="max-w-[140px] truncate">{doc.name}</span>
                    <button onClick={() => set("files", f.files.filter((_, j) => j !== i))}><X size={12} /></button>
                  </div>
                ))}
                <label data-testid="post-files-upload" className="btn-ghost rounded-lg h-9 px-3 text-xs flex items-center gap-1.5 cursor-pointer">
                  {uploading === "files" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add file
                  <input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,.key,.txt,.zip,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "files")} />
                </label>
              </div>
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mt-4">
          <span className="text-[11px] text-sec flex items-center gap-1"><Coins size={12} /> {f.id ? "Editing is free" : "Publishing costs 6 credits"}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost rounded-full h-10 px-5 text-sm">Cancel</button>
            <button data-testid="post-save" onClick={save} disabled={saving} className="btn-primary rounded-full h-10 px-6 text-sm flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} {f.id ? "Save changes" : "Publish"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Detail dialog ---------------- */
function DetailDialog({ v: initial, onClose, onEdit, onBackers, onChanged }) {
  const { user, refreshUser } = useApp();
  const [v, setV] = useState(initial);
  const [tab, setTab] = useState(null); // 'interest' | 'pledge'
  const [msg, setMsg] = useState("");
  const [org, setOrg] = useState("");
  const [contact, setContact] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [liked, setLiked] = useState(initial.liked || false);
  const [likes, setLikes] = useState(initial.likes || 0);
  const [shares, setShares] = useState(initial.shares || 0);
  const isApp = v.listing_type === "app";

  useEffect(() => {
    api.get(`/ventures/${initial.id}`).then(({ data }) => {
      setV(data.venture);
      setLiked(!!data.venture.liked); setLikes(data.venture.likes || 0); setShares(data.venture.shares || 0);
      if (data.venture.charged) { toast(`Spent ${data.venture.charged} credits to view this idea`); refreshUser?.(); }
    }).catch((e) => {
      if (e.response?.status === 402) { toast.error(apiError(e.response?.data?.detail)); onClose(); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.id]);

  const toggleLike = async () => {
    if (!user) { toast.error("Sign in to like."); return; }
    try { const { data } = await api.post(`/ventures/${v.id}/like`); setLiked(data.liked); setLikes(data.likes); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const share = async () => {
    const url = `${window.location.origin}/ventures?id=${v.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it anywhere!"); } catch { toast(url); }
    api.post(`/ventures/${v.id}/share`).then(({ data }) => setShares(data.shares)).catch(() => {});
  };

  const pct = v.funding_goal > 0 ? Math.min(100, Math.round((v.raised / v.funding_goal) * 100)) : 0;

  const submitInterest = async (kind) => {
    if (!user) { toast.error("Sign in to continue."); return; }
    setBusy(true);
    try {
      await api.post(`/ventures/${v.id}/interest`, { message: msg, investor_org: org, contact, kind });
      toast.success(kind === "buy" ? "Sent to the seller — they'll reach out." : "Interest sent — the founder now has your lead.");
      setTab(null); setMsg(""); setOrg(""); setContact("");
      const { data } = await api.get(`/ventures/${v.id}`); setV(data.venture); onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitPledge = async () => {
    if (!user) { toast.error("Sign in to pledge."); return; }
    if (!(parseFloat(amount) > 0)) { toast.error("Enter an amount."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/ventures/${v.id}/pledge`, { amount: parseFloat(amount), currency: v.funding_currency, message: msg });
      toast.success(`Pledged ${money(amount, v.funding_currency)} — thank you for backing this venture!`);
      setV((s) => ({ ...s, raised: data.raised, backers: data.backers })); setTab(null); setAmount(""); setMsg(""); onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this listing?")) return;
    try { await api.delete(`/ventures/${v.id}`); toast.success("Deleted."); onClose(); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const Links = () => (
    <div className="flex flex-wrap gap-2 mt-3">
      {v.demo_url && <a data-testid="detail-demo" href={v.demo_url} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Play size={12} /> Live demo</a>}
      {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Globe size={12} /> Website</a>}
      {v.github && <a href={v.github} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Github size={12} /> GitHub</a>}
      {v.repository && <a href={v.repository} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Github size={12} /> Repo</a>}
      {v.github_profile && <a href={v.github_profile} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Github size={12} /> Founder GitHub</a>}
      {v.video_url && <a href={v.video_url} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Play size={12} /> Video</a>}
      {v.pitch_deck_url && <a href={v.pitch_deck_url} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><FileText size={12} /> Deck</a>}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-rud text-[var(--rud-text)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="venture-detail-dialog">
        {v.cover && <img src={imgUrl(v.cover)} alt="" className="w-full h-36 object-cover rounded-xl" />}
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-elevated border border-rud grid place-items-center overflow-hidden shrink-0">
              {v.logo ? <img src={imgUrl(v.logo)} alt="" className="w-full h-full object-cover" /> : (isApp ? <ShoppingBag size={20} className="text-sec" /> : <Rocket size={20} className="text-sec" />)}
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-serif text-2xl tracking-tight truncate">{v.name}</DialogTitle>
              <div className="flex items-center gap-2 mt-1"><StageBadge stage={v.stage} />{v.sector && <span className="text-xs text-brand">{v.sector}</span>}{v.country && <span className="text-[11px] text-sec flex items-center gap-1"><MapPin size={11} /> {v.country}</span>}</div>
            </div>
          </div>
        </DialogHeader>

        {v.one_liner && <p className="text-sm text-[var(--rud-text)]">{v.one_liner}</p>}
        <Links />
        {(v.files || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="detail-files">
            {v.files.map((doc, i) => (
              <a key={i} href={imgUrl(doc.url)} target="_blank" rel="noreferrer" className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Download size={12} /> {doc.name}</a>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="detail-like" onClick={toggleLike} className={`rounded-full h-8 px-3 text-xs flex items-center gap-1.5 border transition-colors ${liked ? "border-brand text-brand bg-brand/10" : "border-rud text-sec hover:border-brand/50"}`}><ThumbsUp size={13} /> {likes}</button>
          <button data-testid="detail-share" onClick={share} className="rounded-full h-8 px-3 text-xs flex items-center gap-1.5 border border-rud text-sec hover:border-brand/50 transition-colors"><Share2 size={13} /> Share{shares ? ` · ${shares}` : ""}</button>
        </div>

        {/* Funding / price */}
        {isApp ? (
          <div className="rud-card rounded-xl p-4 mt-3 flex items-center justify-between">
            <div><div className="text-[11px] text-sec uppercase tracking-widest">Asking price</div><div className="font-serif text-2xl">{v.sale_price > 0 ? money(v.sale_price, v.sale_currency) : "Open to offers"}</div></div>
            <div className="text-right text-xs text-sec">{v.license_type && <div>{v.license_type}</div>}{v.platform && <div>{v.platform}</div>}{v.tech_stack && <div className="text-brand">{v.tech_stack}</div>}</div>
          </div>
        ) : (
          <div className="rud-card rounded-xl p-4 mt-3">
            <div className="flex items-center justify-between text-sm mb-1"><span>{money(v.raised, v.funding_currency)} raised</span><span className="text-sec">Goal {money(v.funding_goal, v.funding_currency)}</span></div>
            <div className="h-2 rounded-full bg-elevated overflow-hidden"><div className="h-full bg-brand" style={{ width: `${pct}%` }} /></div>
            <div className="flex items-center justify-between mt-2 text-[11px] text-sec">
              <span className="flex items-center gap-1"><Users size={12} /> {v.backers || 0} backers · {v.interest_count || 0} interested</span>
              {v.equity_offered && <span className="text-brand">{v.equity_offered}</span>}
            </div>
          </div>
        )}

        {/* Facts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
          {v.year_started && <Fact label="Started" val={v.year_started} />}
          {v.growth && <Fact label="Growth" val={v.growth} />}
          {v.team_size && <Fact label="Team" val={v.team_size} />}
          {v.traction && <Fact label="Traction" val={v.traction} />}
          {v.potential && <Fact label="Potential" val={v.potential} />}
        </div>

        {v.description && <div className="mt-3"><div className="text-[11px] uppercase tracking-widest text-brand mb-1">About</div><p className="text-sm text-sec whitespace-pre-wrap leading-relaxed">{v.description}</p></div>}

        {(v.screenshots || []).length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-3">{v.screenshots.map((s, i) => <img key={i} src={imgUrl(s)} alt="" className="w-full aspect-[9/16] object-cover rounded-lg border border-rud" />)}</div>
        )}

        {/* Founder */}
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-rud">
          <div className="w-9 h-9 rounded-full bg-elevated border border-rud grid place-items-center overflow-hidden">
            {v.founder_avatar ? <img src={imgUrl(v.founder_avatar)} alt="" className="w-full h-full object-cover" /> : <Building2 size={16} className="text-sec" />}
          </div>
          <div className="min-w-0"><div className="text-sm font-medium truncate">{v.founder_name}</div>{v.founder_headline && <div className="text-[11px] text-sec truncate">{v.founder_headline}</div>}</div>
        </div>

        {/* Actions */}
        {v.is_owner ? (
          <div className="flex flex-wrap gap-2 mt-4">
            <button data-testid="detail-edit" onClick={() => onEdit(v)} className="btn-ghost rounded-full h-10 px-4 text-sm flex items-center gap-1.5"><Pencil size={14} /> Edit</button>
            <button data-testid="detail-backers" onClick={() => onBackers(v)} className="btn-primary rounded-full h-10 px-4 text-sm flex items-center gap-1.5"><Users size={14} /> View leads & backers</button>
            <button data-testid="detail-delete" onClick={del} className="btn-ghost rounded-full h-10 px-4 text-sm flex items-center gap-1.5 text-red-400"><Trash2 size={14} /> Delete</button>
          </div>
        ) : tab === "interest" || tab === "pledge" ? (
          <div className="rud-card rounded-xl p-4 mt-4 space-y-2">
            {tab === "pledge" && <input data-testid="pledge-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount in ${v.funding_currency}`} className="rud-input w-full rounded-lg px-3 py-2 text-sm" />}
            {tab === "interest" && <input data-testid="interest-org" value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Your fund / company (optional)" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />}
            {tab === "interest" && <input data-testid="interest-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="How the founder can reach you — email / telegram" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />}
            <textarea data-testid="interest-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder={tab === "pledge" ? "Message to the founder (optional)" : isApp ? "Tell the seller what you need / your offer" : "Introduce yourself to the founder"} className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setTab(null)} className="btn-ghost rounded-full h-9 px-4 text-sm">Cancel</button>
              <button data-testid="interest-submit" onClick={() => tab === "pledge" ? submitPledge() : submitInterest(isApp ? "buy" : "interest")} disabled={busy} className="btn-primary rounded-full h-9 px-5 text-sm flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : tab === "pledge" ? <HandCoins size={14} /> : <Heart size={14} />} {tab === "pledge" ? "Confirm pledge" : "Send"}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mt-4">
            {isApp ? (
              <button data-testid="detail-buy" onClick={() => setTab("interest")} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2"><ShoppingBag size={15} /> Buy / contact seller</button>
            ) : (
              <>
                <button data-testid="detail-interest" onClick={() => setTab("interest")} className="btn-ghost rounded-full h-10 px-5 text-sm flex items-center gap-2"><Heart size={15} /> Express interest{v.my_interest ? " ✓" : ""}</button>
                <button data-testid="detail-pledge" onClick={() => setTab("pledge")} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2"><HandCoins size={15} /> Pledge</button>
              </>
            )}
            {v.contact_email && <a href={`mailto:${v.contact_email}`} className="btn-ghost rounded-full h-10 px-4 text-sm flex items-center gap-1.5"><ExternalLink size={14} /> Contact</a>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, val }) {
  return <div className="rounded-lg border border-rud bg-elevated p-2.5"><div className="text-[10px] uppercase tracking-widest text-sec">{label}</div><div className="text-sm font-medium mt-0.5 line-clamp-2">{val}</div></div>;
}

/* ---------------- Backers / leads dialog (owner) ---------------- */
function BackersDialog({ v, onClose }) {
  const { refreshUser } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/ventures/${v.id}/backers`).then(({ data }) => {
      setData(data);
      if (data.charged) { toast(`Spent ${data.charged} credits to view leads`); refreshUser?.(); }
    }).catch((e) => {
      if (e.response?.status === 402) { toast.error(apiError(e.response?.data?.detail)); onClose(); }
      else setData({ leads: [], pledges: [] });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-rud text-[var(--rud-text)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="venture-backers-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight">Leads & backers · {v.name}</DialogTitle></DialogHeader>
        {!data ? <div className="flex items-center gap-2 text-sec"><Loader2 size={15} className="animate-spin" /> Loading…</div> : (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-brand mb-2">Interested ({data.leads.length})</div>
              {data.leads.length === 0 ? <p className="text-sm text-sec">No leads yet.</p> : data.leads.map((l) => (
                <div key={l.id} className="rud-card rounded-xl p-3 mb-2">
                  <div className="flex items-center gap-2"><span className="font-medium text-sm">{l.investor_name}</span>{l.investor_org && <span className="text-[11px] text-sec">· {l.investor_org}</span>}<span className="ml-auto text-[10px] uppercase tracking-widest text-brand">{l.kind}</span></div>
                  {l.message && <p className="text-xs text-sec mt-1">{l.message}</p>}
                  {l.investor_email && <a href={`mailto:${l.investor_email}`} className="text-[11px] text-brand mt-1 inline-block mr-3">{l.investor_email}</a>}
                  {l.investor_contact && <span className="text-[11px] text-brand mt-1 inline-block">{l.investor_contact}</span>}
                </div>
              ))}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-brand mb-2">Pledges ({data.pledges.length})</div>
              {data.pledges.length === 0 ? <p className="text-sm text-sec">No pledges yet.</p> : data.pledges.map((p) => (
                <div key={p.id} className="rud-card rounded-xl p-3 mb-2 flex items-center justify-between">
                  <div><span className="font-medium text-sm">{p.backer_name}</span>{p.message && <p className="text-xs text-sec mt-0.5">{p.message}</p>}</div>
                  <span className="text-brand font-semibold">{money(p.amount, p.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
